import type { Abonnement, Client, Course, Creneau, Forfait, StatutClient } from './types'

/**
 * Abonnements des clients PME et fiche client (migration 0031) — logique PURE, testée.
 *
 * Les règles de fond (prix figés, un seul abonnement courant, changement au 1er du mois suivant, pause qui
 * court jusqu'à la fin du mois) sont appliquées PAR LA BASE ; ce module lit l'historique, explique à la
 * personne ce qui va se passer, et traduit les refus.
 */

/** L'abonnement COURANT : celui qui n'a pas de date de fin. Il peut ne pas avoir commencé (changement programmé). */
export function abonnementCourant(abonnements: readonly Abonnement[], clientId: string): Abonnement | null {
  return abonnements.find((a) => a.client_account_id === clientId && a.date_fin === null) ?? null
}

/**
 * L'abonnement en vigueur AUJOURD'HUI : celui dont la période couvre `jour` (AAAA-MM-JJ). Entre un
 * abonnement qui vient de finir et la reprise, il n'y en a pas.
 */
export function abonnementEnVigueur(abonnements: readonly Abonnement[], clientId: string, jour: string): Abonnement | null {
  return (
    abonnements
      .filter((a) => a.client_account_id === clientId && a.date_debut <= jour && (a.date_fin === null || a.date_fin >= jour))
      .sort((a, b) => b.date_debut.localeCompare(a.date_debut))[0] ?? null
  )
}

/** Un changement de forfait PROGRAMMÉ : une période courante qui commence dans le futur. */
export function changementProgramme(abonnements: readonly Abonnement[], clientId: string, jour: string): Abonnement | null {
  const c = abonnementCourant(abonnements, clientId)
  return c && c.date_debut > jour ? c : null
}

/** L'historique d'un client, le plus récent d'abord. */
export function historiqueAbonnements(abonnements: readonly Abonnement[], clientId: string): Abonnement[] {
  return abonnements.filter((a) => a.client_account_id === clientId).sort((a, b) => b.date_debut.localeCompare(a.date_debut))
}

/** Un client PME actif sans aucun abonnement en vigueur ni programmé : ses courses ne seront PAS facturées par le mois. */
export function pmeSansAbonnement(client: Pick<Client, 'id' | 'type' | 'status'>, abonnements: readonly Abonnement[], jour: string): boolean {
  if (client.type !== 'pme' || client.status !== 'active') return false
  return !abonnementEnVigueur(abonnements, client.id, jour) && !abonnementCourant(abonnements, client.id)
}

/** Le libellé d'un plan : celui du forfait de l'organisation, sinon celui de la graine d'origine, sinon le code. */
const LIBELLE_PLAN_GRAINE: Record<string, string> = {
  particulier: 'Particulier (à la course)',
  pme_essentiel: 'PME Essentiel',
  pme_business: 'PME Business',
  pme_flotte: 'PME Sur mesure',
}
export function libelleDuPlan(code: string | null | undefined, forfaits: readonly Pick<Forfait, 'code' | 'libelle'>[]): string {
  if (!code) return '—'
  return forfaits.find((f) => f.code === code)?.libelle ?? LIBELLE_PLAN_GRAINE[code] ?? code
}

/**
 * Courses livrées d'un client dans un mois (`AAAA-MM`, UTC = Bamako), pas encore portées par une facture : ce que le
 * quota du mois a consommé jusqu'ici — la même règle que la facturation (une course facturée à part ne compte pas).
 */
export function coursesDuMois(
  livrees: readonly Pick<Course, 'client_account_id' | 'id' | 'delivered_at' | 'created_at'>[],
  clientId: string,
  mois: string,
  facturees: ReadonlySet<string>,
): number {
  return livrees.filter(
    (c) => c.client_account_id === clientId && !facturees.has(c.id) && (c.delivered_at ?? c.created_at).slice(0, 7) === mois,
  ).length
}

/** « 25 000 F · 40 courses » — le forfait tel que FIGÉ sur l'abonnement. */
export function descriptionConditions(a: Pick<Abonnement, 'prix_mensuel_fcfa' | 'quota_courses'>, fcfa: (n: number) => string): string {
  return `${fcfa(a.prix_mensuel_fcfa)} par mois · ${a.quota_courses === null ? 'courses illimitées' : `${a.quota_courses} course${a.quota_courses > 1 ? 's' : ''} incluse${a.quota_courses > 1 ? 's' : ''}`}`
}

/** Le forfait de la grille, affiché dans les listes : « PME Essentiel — 25 000 F/mois · 40 courses ». */
export function libelleForfait(f: Pick<Forfait, 'libelle' | 'prix_mensuel_fcfa' | 'quota_courses'>, fcfa: (n: number) => string): string {
  const prix = f.prix_mensuel_fcfa === null ? 'sur devis' : `${fcfa(f.prix_mensuel_fcfa)}/mois`
  const quota = f.prix_mensuel_fcfa === null ? '' : f.quota_courses === null ? ' · illimité' : ` · ${f.quota_courses} courses`
  return `${f.libelle} — ${prix}${quota}`
}

/** Le forfait est-il « sur devis » (prix négocié, porté par l'abonnement) ? */
export function estSurDevis(f: Pick<Forfait, 'prix_mensuel_fcfa'> | undefined): boolean {
  return !!f && f.prix_mensuel_fcfa === null
}

/** Ce qui va se passer quand on change le statut de la fiche — dit AVANT le clic. */
export function consequenceStatut(vers: StatutClient, aUnAbonnement: boolean): string {
  if (vers === 'active')
    return aUnAbonnement
      ? "L'abonnement reprend : s'il court encore ce mois-ci il est rouvert tel quel, sinon une nouvelle période démarre aujourd'hui aux conditions figées d'avant (mois entier dû)."
      : 'Le client redevient proposé à la prise d’appel.'
  const quoi = vers === 'paused' ? 'La pause' : 'La clôture'
  return aUnAbonnement
    ? `${quoi} prend effet à la FIN DU MOIS EN COURS : le mois en cours reste dû en entier. Un changement de forfait programmé est annulé.`
    : `${quoi} retire le client de la prise d’appel.`
}

export const LIBELLE_STATUT_CLIENT: Record<StatutClient, string> = {
  active: 'Actif',
  paused: 'En pause',
  closed: 'Clos',
}

export const PASTILLE_STATUT_CLIENT: Record<StatutClient, string> = {
  active: 'b-green',
  paused: 'b-warn',
  closed: 'b-gray',
}

/* ------------------------------------------------------------------ créneaux */

/** L'écart entre les créneaux enregistrés et ceux de l'écran : de quoi écrire SANS tout supprimer puis recréer. */
export function diffCreneaux(
  avant: readonly Creneau[],
  apres: readonly Creneau[],
): { ajouter: Creneau[]; modifier: (Creneau & { id: string })[]; supprimer: string[] } {
  const ajouter: Creneau[] = []
  const modifier: (Creneau & { id: string })[] = []
  const gardes = new Set<string>()
  for (const c of apres) {
    if (!c.id) {
      ajouter.push(c)
      continue
    }
    gardes.add(c.id)
    const ancien = avant.find((x) => x.id === c.id)
    if (!ancien || ancien.weekday !== c.weekday || ancien.start_time.slice(0, 5) !== c.start_time.slice(0, 5) || ancien.end_time.slice(0, 5) !== c.end_time.slice(0, 5))
      modifier.push({ ...c, id: c.id })
  }
  const supprimer = avant.filter((c) => c.id && !gardes.has(c.id)).map((c) => c.id as string)
  return { ajouter, modifier, supprimer }
}

/** Créneaux valides ? Début < fin, et pas deux créneaux qui se recouvrent le même jour. Vide = acceptable. */
export function controlerCreneaux(creneaux: readonly Creneau[]): string[] {
  const motifs: string[] = []
  for (const c of creneaux) if (!(c.start_time < c.end_time)) motifs.push('Un créneau doit finir après son début.')
  const parJour = new Map<number, Creneau[]>()
  for (const c of creneaux) parJour.set(c.weekday, [...(parJour.get(c.weekday) ?? []), c])
  for (const liste of parJour.values()) {
    const tri = [...liste].sort((a, b) => a.start_time.localeCompare(b.start_time))
    for (let i = 1; i < tri.length; i++) {
      const prec = tri[i - 1]
      const cur = tri[i]
      if (prec && cur && cur.start_time < prec.end_time) motifs.push('Deux créneaux du même jour se recouvrent.')
    }
  }
  return [...new Set(motifs)]
}

/* ------------------------------------------------------------- fiche client */

export interface FicheClient {
  name: string
  contact_name: string
  phone: string
  email: string
  quartier: string
  commune: string
  notes: string
  offslot_surcharge_fcfa: string
}

/** Motifs de refus d'une fiche avant envoi (vide = acceptable). La base vérifie aussi ses propres contraintes. */
export function controlerFiche(f: FicheClient, type: Client['type']): string[] {
  const motifs: string[] = []
  if (!f.name.trim()) motifs.push(type === 'pme' ? 'La raison sociale est obligatoire.' : 'Le nom est obligatoire.')
  if (!f.phone.trim()) motifs.push('Le téléphone est obligatoire (clé de la prise d’appel).')
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) motifs.push('Adresse e-mail invalide.')
  const supp = Number(f.offslot_surcharge_fcfa.replace(/\s/g, '').replace(',', '.'))
  if (type === 'pme' && (!Number.isInteger(supp) || supp < 0)) motifs.push('Le supplément hors créneau est un nombre entier de francs, positif ou nul.')
  return motifs
}

/** Les refus de la base pour les abonnements, en français. */
const MESSAGES_ABONNEMENT: Record<string, string> = {
  forbidden: "Votre rôle ne permet pas cette opération sur les abonnements.",
  client_non_pme: 'Un forfait mensuel ne concerne que les clients PME ; un particulier paie à la course.',
  client_inactif: 'Ce client est en pause ou clos : réactivez-le d’abord.',
  abonnement_existant: 'Ce client a déjà un abonnement courant : changez son forfait plutôt que d’en souscrire un second.',
  aucun_abonnement: "Ce client n'a pas d'abonnement courant : souscrivez-le d'abord.",
  forfait_inconnu: 'Forfait inconnu.',
  forfait_inactif: "Ce forfait n'est plus proposé (désactivé dans Réglages).",
  devis_reserve_au_proprietaire: 'Un forfait sur devis (prix négocié) est fixé par le propriétaire ou le comptable.',
  prix_devis_requis: 'Un forfait sur devis demande un prix mensuel négocié.',
  quota_invalide: 'Le quota doit être un entier d’au moins 1 (vide = illimité).',
  prix_fige_par_le_forfait: 'Ce forfait est au prix de la grille : le prix et le quota ne se négocient que sur un forfait « sur devis ».',
  jour_facturation_invalide: 'Le jour de facturation est compris entre 1 et 28.',
  date_debut_invalide: 'La date de début est trop éloignée (un an au plus).',
  effet_invalide: "Le changement prend effet le 1er d'un mois, à partir du mois prochain.",
  abonnement_chevauchant: "Une autre période d'abonnement de ce client couvre déjà ces dates.",
}

export function traduireErreurAbonnement(erreur: unknown): string {
  const o = (erreur && typeof erreur === 'object' ? erreur : {}) as { message?: unknown; code?: unknown }
  const message = typeof o.message === 'string' ? o.message : typeof erreur === 'string' ? erreur : ''
  for (const [code, phrase] of Object.entries(MESSAGES_ABONNEMENT)) if (message.includes(code)) return phrase
  if (/Could not find the function|PGRST202|function .* does not exist/i.test(message))
    return 'Disponible après la mise à jour de la base (migration 0031).'
  return message || 'Erreur inconnue.'
}
